<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Calendar</h1>
      </div>
    </div>

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12 col-lg-6">
          <ul class="calendar-legend list-none">
            <li><span class="booked"></span> Booked Event</li>
            <li><span class="pending"></span> Request {{ $userIsClient ? 'Awaiting My Response' : this.$activeClientModuleEnabled ? 'Awaiting Client Response' : 'Awaiting Agent Response' }}</li>
            <li><span class="cancelled"></span> Request {{$userIsTalent ? 'Awaiting My Response' : `Awaiting ${$options.filters.sentenceCase($starLabel)} Response` }}</li>
          </ul>
        </div>
        <div class="col-sm-12 col-lg-6">
          <template v-if="$userIsAdmin || $userIsManager">
            <h3 class="m-t-none">Show {{ $starLabel | sentenceCase }} Calendars</h3>

            <selectize
              class="form-control selectize selectize-multiple"
              name="talent"
              v-model="talentCalendars"
              :options="talentOptions"
              multiple
              :pre-load="true"
              :fetch-results="fetchTalentCallback"
              :fetch-more-results="fetchMoreTalentCallback"/>
          </template>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-12">
          <div class="fc-loading-container">
            <pickstar-loading/>
          </div>
          <full-calendar
            ref="calendar"
            :event-sources="eventSources"
            :selectable="false"
            :editable="false"
            :config="config"
            @day-click="handleDayClickCallback"
            @event-selected="handleEventSelectedCallback"/>
        </div>
      </div>

      <div class="margin-20"></div>
    </div>
  </div>
</template>

<style scoped>
.loading-container {
  display: none;
}
</style>

<script>
import { mapActions, mapState } from 'vuex'
import { map, debounce } from 'lodash'
import { FullCalendar } from 'vue-full-calendar'
import Vue from 'vue'
import CalendarDayBreakdown from './CalendarDayBreakdown'
import CalendarMonthEvent from './CalendarMonthEvent'
import Selectize from '../ext/codium/CodiumSelectize'
import $ from 'jquery'
import moment from 'moment'
import 'utils/table-scanner'

export default {
  data () {
    return {
      talentCalendars: [],
      canClickDays: true,
      selectedDate: null,
      config: {
        themeSystem: 'bootstrap3',
        aspectRatio: 1,
        height: 'auto',
        defaultView: 'month',
        fixedWeekCount: false,
        allDaySlot: false,
        timezone: 'local',
        header: {
          left: 'title',
          center: '',
          right: 'prev,today,next'
        },
        views: {
          month: {
            columnHeaderFormat: 'dddd'
          }
        },
        eventRender: (event, element, view) => {
          let component = () => {
            switch (view.type) {
              case 'month':
                return CalendarMonthEvent
            }
          }

          return new Vue({
            router: this.$router,
            render (h) {
              return h(component(), {
                props: {
                  event
                }
              })
            }
          }).$mount().$el
        },

        eventAfterAllRender: (view) => {
          $(this.$refs.calendar.$el).find('.fc-content-skeleton').each((index, element) => {
            // Event containers are the first row of cells, though some won't have events.
            let eventContainers = $('td', $(element).find('table > tbody > tr:first-child'))

            // Spin through every other event container that's not within the first row. These
            // will be shifted up into the first row at the corresponding cell position.
            $(element).find('table > tbody > tr:not(:first-child) > .fc-event-container').each((index, element) => {
              $('.fc-day-grid-event', $(element)).appendTo(eventContainers.eq($(element).cellPos().left))
            })

            // Remove the excess rows and the row spans on remaining cells.
            $(element).find('table > tbody > tr:not(:first-child)').remove()
            $(element).find('table > tbody > tr > td').removeAttr('rowspan')
          })

          this.$refs.calendar.fireMethod('render')
        },

        loading: (loading, view) => {
          $(this.$refs.calendar.$el).find('.fc-loading-container').toggle(loading)
        }
      },
      eventSources: [
        {
          events: (start, end, timezone, callback) => {
            const params = {
              from: start.unix(),
              to: end.unix(),
              talent: this.talentCalendars
            }

            this.getCalendarBookings(params)
              .then(() => {
                this.createEventObjectsForCalendar(callback)
              })
          }
        }
      ]
    }
  },

  components: {
    FullCalendar,
    Selectize
  },

  computed: {
    ...mapState('calendar', ['bookings']),
    ...mapState('talent', ['talents']),
    ...mapState('app', ['loading']),

    talentOptions () {
      return map(this.talents, talent => ({ id: talent.id, text: talent.name }))
    }
  },

  watch: {
    loading () {
      this.$refs.calendar.fireMethod('render')
    },

    talentCalendars: debounce(function () {
      this.deselectAndHideCollapse()

      this.$refs.calendar.fireMethod('refetchEvents')
    }, 800)
  },

  mounted () {
    $('.fc-loading-container').appendTo($(this.$refs.calendar.$el))
  },

  methods: {
    ...mapActions('calendar', ['getCalendarBookings']),
    ...mapActions('talent', ['getTalents', 'getNextTalents']),

    fetchTalentCallback (name, callback) {
      return this.getTalents({ name })
        .then(() => {
          callback(this.talentOptions)
        })
    },

    fetchMoreTalentCallback (name, callback) {
      return this.getNextTalents({ name })
        .then(() => {
          callback(this.talentOptions)
        })
        .catch(() => {})
    },

    getEventsForDate (date) {
      return this.$refs.calendar.fireMethod('clientEvents', (event) => {
        return event.start.isSame(date, 'day')
      })
    },

    handleEventSelectedCallback (event, jsEvent, view) {
      if (view.name !== 'month') {
        return
      }

      this.handleDayClickCallback(event.start, jsEvent, view)
    },

    handleDayClickCallback (date, jsEvent, view) {
      if (!this.canClickDays) {
        return
      }

      let events = this.getEventsForDate(date)

      if (events.length === 0) {
        return
      }

      this.canClickDays = false

      let datesInSameWeek = this.selectedDate && this.selectedDate.isSame(date, 'week')
      let datesAreSame = this.selectedDate && this.selectedDate.isSame(date, 'day')
      let closestRowDiv = $(jsEvent.target).closest('.fc-row')
      let containerDiv = $(this.$refs.calendar.$el)
      let collapseDiv = containerDiv.find('.collapse')

      this.selectedDate = date

      // Toggle the selected class on the date that was clicked.
      containerDiv.find('.fc-selected').removeClass('fc-selected')
      closestRowDiv.find(`.fc-bg [data-date=${date.format('YYYY-MM-DD')}]`).addClass('fc-selected')

      // When the date selected is the same as the currently selected date OR they are not in the same
      // week then we'll hide the existing collapse and then remove it.
      if (datesAreSame || !datesInSameWeek) {
        collapseDiv.collapse('hide').one('hidden.bs.collapse', (jsEvent) => {
          $(jsEvent.target).remove()
        })

        // If the dates are the same then we'll null out the selected date and allow clicking on days
        // again. This is so you can toggle the same day by clicking it over and over.
        if (datesAreSame) {
          this.selectedDate = null
          this.canClickDays = true

          return
        }
      }

      if (!datesInSameWeek) {
        // Create and append the new collapse div to the DOM, register the shown event so we can
        // enable clicking on days again once it has shown.
        collapseDiv = $('<div class="collapse"></div>')
        collapseDiv.one('shown.bs.collapse', () => {
          this.canClickDays = true
        })

        closestRowDiv.after(collapseDiv)
      } else {
        this.canClickDays = true
      }

      // The actual breakdown of what is happening on a given day is controlled from a Vue component so we
      // can still have reactiveness and all that Vue goodness. We'll render this off-document and then
      // append the component to the DOM with jQuery.
      let component = new Vue({
        router: this.$router,
        render (h) {
          return h(CalendarDayBreakdown, {
            props: {
              events
            }
          })
        }
      }).$mount()

      collapseDiv.html(component.$el)

      collapseDiv.collapse('show')
    },

    deselectAndHideCollapse () {
      this.selectedDate = null

      let containerDiv = $(this.$refs.calendar.$el)

      containerDiv.find('.collapse').collapse('hide').one('hidden.bs.collapse', (event) => {
        $(event.target).remove()
      })
      containerDiv.find('.fc-selected').removeClass('fc-selected')

      this.$refs.calendar.fireMethod('unselect')
    },

    createEventObjectsForCalendar (callback) {
      callback(map(this.bookings, (booking) => {
        return {
          title: booking.opportunity_name,
          start: moment.utc(booking.utc_date, 'YYYY-MM-DD HH:mm:ss').toISOString(),
          booking
        }
      }))
    }
  }
}
</script>
